import React, { useCallback, useMemo } from 'react';
import { ChannelDetail } from '@/components/widget/ChannelDetail';
import ChatLandingPage from '@/components/widget/ChatLandingPage';
import WidgetWindow from '@/components/widget/WidgetWindow';
import useChannels from '@/hooks/useChannels';
import { useTracking } from '@/hooks/useTracking';
import { useWidgetUIStore } from '@/hooks/useWidgetUIStore';
import { ChannelOptions } from '@/types/widgetConfig';
import {
  WidgetNavigationActions,
  WidgetOpeningManagerProps,
  WidgetViewState,
} from './types';

const WidgetOpeningManager: React.FC<WidgetOpeningManagerProps> = ({
  widgetConfig,
}) => {
  const { currentView, actions: widgetUIActions } = useWidgetUIStore();
  const { selectChannel } = useChannels();
  const { setReferralPage } = useTracking();

  // Navigation actions
  const navigationActions: WidgetNavigationActions = useMemo(
    () => ({
      goToLandingPage: () => {
        widgetUIActions.setCurrentView(WidgetViewState.LANDING_PAGE);
      },
      goToChatWindow: () => {
        widgetUIActions.setCurrentView(WidgetViewState.CHAT_WINDOW);
      },
      goToChannelDetail: (channelType: keyof ChannelOptions) => {
        selectChannel(channelType);
      },
      minimize: () => {
        widgetUIActions.setCurrentView(WidgetViewState.MINIMIZED);
      },
    }),
    [widgetUIActions, selectChannel],
  );

  // Handle channel selection - now navigates to detail view
  const handleChannelSelect = useCallback(
    (channel: string) => {
      navigationActions.goToChannelDetail(channel as keyof ChannelOptions);
    },
    [navigationActions],
  );

  // Handle "Start chat" from landing page
  const handleStartChat = useCallback(() => {
    setReferralPage();
    navigationActions.goToChatWindow();
  }, [navigationActions, setReferralPage]);

  // Handle back navigation from chat window to landing page
  const handleBackToLanding = useCallback(() => {
    // Always go back to landing page when back button is clicked
    navigationActions.goToLandingPage();
  }, [navigationActions]);

  // Handle contact other channels from chat window
  const handleContactOtherChannels = useCallback(
    (channel: string) => {
      handleChannelSelect(channel);
    },
    [handleChannelSelect],
  );

  // Render based on current view - no need for isVisible check
  const renderCurrentView = () => {
    switch (currentView) {
      case WidgetViewState.LANDING_PAGE:
        return (
          <ChatLandingPage
            onStartChat={handleStartChat}
            onChannelSelect={handleChannelSelect}
            onMinimize={navigationActions.minimize}
          />
        );

      case WidgetViewState.CHAT_WINDOW:
        return (
          <WidgetWindow
            widgetConfig={widgetConfig}
            onBack={handleBackToLanding}
            onMinimize={navigationActions.minimize}
            onContactOtherChannels={handleContactOtherChannels}
          />
        );

      case WidgetViewState.CHANNEL_DETAIL:
        return (
          <ChannelDetail
            onBack={navigationActions.goToLandingPage}
            onContinue={() => {
              // Close widget after opening external channel
              navigationActions.minimize();
            }}
          />
        );

      case WidgetViewState.MINIMIZED:
        return null;

      default:
        return null;
    }
  };

  return (
    <div className="sf:w-full sf:h-full sf:rounded-2xl sf:shadow-widget">
      {renderCurrentView()}
    </div>
  );
};

export default WidgetOpeningManager;
